/**
 * YBM Studios — BYOK Provider Map
 * ----------------------------------------------------------------
 * Single source of truth for "which keys do I need and what does
 * each one unlock?" Used by both the landing-page BYOK explainer
 * section and the in-app Setup Checklist on /settings/api-keys.
 *
 * Keep entries customer-facing — no internal SDK names, no jargon.
 */
import { Brain, Image as ImageIcon, Mic, Video, Sparkles, Wand2 } from "lucide-react";

export const BYOK_PROVIDERS = [
  {
    id: "openai",
    name: "OpenAI",
    summary: "Writing, image, voice, and ad-script generation.",
    unlocks: [
      "Ad-script drafting (Claude is the default, OpenAI is the fallback)",
      "Image gen routes (where applicable) when Gemini is unavailable",
      "Voiceover via OpenAI TTS",
      "Future: direct Sora 2 video the day OpenAI opens partner access",
    ],
    where: "https://platform.openai.com/api-keys",
    icon: Brain,
    color: "from-emerald-500/15 to-emerald-500/0",
    accent: "text-emerald-300",
    note: "Sora 2 video is currently proxied through Emergent's gateway because OpenAI hasn't opened public Sora API yet. The moment they do, your OpenAI key flips Sora onto your account with zero further setup.",
  },
  {
    id: "gemini",
    name: "Google Gemini",
    summary: "Primary image generator (Nano Banana) + writing fallback.",
    unlocks: [
      "Image generation via Gemini Nano Banana (gemini-3.1-flash-image-preview)",
      "Logo generation",
      "Text generation fallback for Wordsmith and ad scripts",
    ],
    where: "https://aistudio.google.com/apikey",
    icon: ImageIcon,
    color: "from-amber-500/15 to-amber-500/0",
    accent: "text-amber-300",
    note: "Recommended primary key — Nano Banana drives every image render on the platform.",
  },
  {
    id: "anthropic",
    name: "Anthropic Claude",
    summary: "Premium writing, scripts and reasoning (Sonnet 4.5).",
    unlocks: [
      "Ad-script generation (Claude Sonnet 4.5 — default)",
      "Wordsmith long-form drafting",
      "Director storyboard + scene-script stages",
    ],
    where: "https://console.anthropic.com/settings/keys",
    icon: Wand2,
    color: "from-violet-500/15 to-violet-500/0",
    accent: "text-violet-300",
    note: "Strongest narrative voice on the platform — recommended for serious commercial copy.",
  },
  {
    id: "elevenlabs",
    name: "ElevenLabs",
    summary: "Voiceover, dialogue and AI music.",
    unlocks: [
      "Voiceover generation with ElevenLabs voices",
      "Dialogue + narration for ads and music videos",
      "Cinema-tier voice quality (when you use the Cinema preset)",
    ],
    where: "https://elevenlabs.io/app/settings/api-keys",
    icon: Mic,
    color: "from-cyan-500/15 to-cyan-500/0",
    accent: "text-cyan-300",
    note: "Voiceover defaults to OpenAI TTS until your ElevenLabs key is connected; paste it here to upgrade voice quality.",
  },
  {
    id: "fal",
    name: "fal.ai",
    summary: "Specialised image/video model routing for cost optimisation.",
    unlocks: [
      "Alternate image generation models when you want a non-Gemini look",
      "Cost-routed video and image workflows (future)",
      "Specialty model selection per-render",
    ],
    where: "https://fal.ai/dashboard/keys",
    icon: Sparkles,
    color: "from-rose-500/15 to-rose-500/0",
    accent: "text-rose-300",
    note: "Optional — only paste if you want to bypass Gemini for image gen or use specialty fal models.",
  },
];

export const PREMIUM_RECOMMENDED = ["openai", "gemini", "anthropic", "elevenlabs"];
